/* eslint-disable no-underscore-dangle */
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import { getSidebarStatus, getSize, setSidebarStatus, setLanguage, setSize } from '@tencent/s2-client-vue-lib';
import { getLocale } from '@/lang';
import store from '@/store';
import { IAttachmentList, IAttachmentEvent, IAttachmentTableData } from '@/components/upload-attachment/interfaces/IAttachmentList';

/** 403数据结构 */
export interface IForbiddenData {
	isForbidden: boolean;
	error: any;
}

/**
 * 场景模式数据结构对照类
 *
 * @export
 * @class SceneConstType
 * @template T
 */
export class SceneConstType {
	private _sceneList: Array<ISceneOption>;
	private _sceneListCSIG: Array<ISceneOption>;
	private _sceneListIEG: Array<ISceneOption>;
	private _sceneListPCG: Array<ISceneOption>;

	public static SCENE_QCP: string = 'QCP';
	public static MODEL_CDP: string = 'CDP';
	public static SCENE_IEG: string = 'MPP';
	public static MODEL_MPP: string = 'MPP';
	public static MODEL_CSIG: string = 'CSIG';
	public static MODEL_BUBUY: string = 'UBUY';
	public static BUSINESSLINE_CDP: string = 'TEST';
	public static UBUY_URL: string = 'http://bps.oa.com';

	// BG SOURCE CODE
	public static CDG: string = 'CDG';
	public static IEG: string = 'IEG';
	public static TEG: string = 'TEG';
	public static WXG: string = 'WXG';
	public static CSIG: string = 'CSIG';
	public static PCG: string = 'PCG';
	public static SSV: string = 'SSV';

	/**
	 * init
	 *
	 * @memberOf SceneConstType
	 */
	constructor() {
		// eslint-disable-next-line no-underscore-dangle
		// 原来默认的值
		// this._sceneList = [this.SCENE_QCP, this.SCENE_MPP, this.SCENE_SPP, this.SCENE_SGP, this.SCENE_EAP, this.SCENE_PPP, this.SCENE_SMP];

		// CSIG
		this._sceneListCSIG = [this.SCENE_SMP, this.SCENE_SPP];
		this._sceneList = this._sceneListCSIG;

		// IEG
		this._sceneListIEG = [this.SCENE_MPP];

		// PCG
		this._sceneListPCG = [this.SCENE_CDP];
	}

	/**
	 * 获取场景模式列表
	 *
	 * @type {Array<ISceneOption>}
	 * @memberOf SceneConstType
	 */
	public get sceneList(): Array<ISceneOption> {
		// eslint-disable-next-line no-underscore-dangle
		return this.getBgSourceDataByID();
	}

	/** 依据BG代码获取BG源数据 */
	public getBgSourceDataByID(id: string = ''): Array<ISceneOption> {
		let sourceData: Array<ISceneOption> = [];
		switch (id) {
			case SceneConstType.CSIG:
				sourceData = this._sceneListCSIG;
				break;
			case SceneConstType.IEG:
				sourceData = this._sceneListIEG;
				break;
			case SceneConstType.PCG:
				sourceData = this._sceneListPCG;
				break;

			default:
				sourceData = this._sceneList;
				break;
		}

		return sourceData;
	}

	/**
	 * 内容设计:Content Design Purchase
	 */
	public get SCENE_CDP(): ISceneOption {
		return {
			sceneName: '内容设计采购',
			sceneCode: SceneConstType.SCENE_QCP,
			iconImg: 'content-design-normal',
			modeList: [
				{
					modelName: 'QCP-内容设计采购',
					modelCode: SceneConstType.MODEL_CDP,
					processList: ['发起采购决策', '下达采购需求', '需求受理', '维护采购订单', '确认验收单', '发起账单'],
					iconImg: 'content-design-drocurement.png',
					businessLineList: [
						{
							modelName: '业务线:QCP',
							modelCode: 'PCG',
							iconImg: 'content-design-drocurement.png'
						}
					]
				}
			]
		};
	}

	/**
	 * 营销推广：Marketing Promotion Purchase
	 */
	public get SCENE_MPP(): ISceneOption {
		return {
			sceneName: '营销推广采购',
			sceneCode: 'MPP',
			iconImg: 'marketing-normal',
			modeList: [
				{
					modelName: '营销推广类',
					modelDesc: '(公共传播、媒介投放、网络推广、名人合作等)',
					// modelCode: 'XXXX',
					modelCode: 'MPP',
					processList: ['发起采购决策'],
					iconImg: 'marketing-purchase.png',
					businessLineList: [
						{
							modelName: 'IEG采购',
							modelCode: SceneConstType.IEG,
							iconImg: 'content-design-drocurement.png'
						}
					]
				}
			]
		};
	}

	/**
	 * 服务采购：Service Purchase Puchase
	 */
	public get SCENE_SPP(): ISceneOption {
		return {
			sceneName: '服务采购',
			sceneCode: 'IDC',
			iconImg: 'demand-acceptance-select',
			modeList: [
				{
					modelName: 'IDC资源采购',
					modelCode: 'SPP',
					processList: ['发起采购决策'],
					iconImg: 'administrative-property.png',
					businessLineList: [
						{
							modelName: '业务线 IDC',
							modelCode: 'CSIG',
							iconImg: 'content-design-drocurement.png'
						}
					]
				}
			]
		};
	}

	/**
	 * 销售商品:Sale Goods Purchase
	 */
	public get SCENE_SGP(): ISceneOption {
		return {
			sceneName: '销售商品采购',
			sceneCode: 'SGP',
			iconImg: 'sale-goods-purchase-normal',
			modeList: [
				{
					modelName: 'CSIG-服务器与供应链采购',
					modelCode: 'SPP',
					processList: ['发起采购决策'],
					iconImg: 'administrative-property.png',
					businessLineList: [
						{
							modelName: 'CSIG-服务器与供应链采购',
							modelCode: '',
							iconImg: 'content-design-drocurement.png'
						}
					]
				}
			]
		};
	}

	/**
	 * 权益资产采购：Equity Assets Purchase
	 */
	public get SCENE_EAP(): ISceneOption {
		return {
			sceneName: '权益采购',
			sceneCode: 'SGP',
			iconImg: 'equity-purchase',
			modeList: []
		};
	}

	/**
	 * 项目采购：Project Purchase
	 */
	public get SCENE_PPP(): ISceneOption {
		return {
			sceneName: '项目采购',
			sceneCode: 'PPP',
			iconImg: 'sale-goods-purchase',
			modeList: []
		};
	}

	/**
	 * 标准物资采购：Standard Material Purcahse
	 */
	public get SCENE_SMP(): ISceneOption {
		return {
			sceneName: '常规物资采购',
			sceneCode: SceneConstType.MODEL_CSIG,
			iconImg: 'conventional-supplies-normal',
			modeList: [
				{
					modelName: 'CSIG-服务器与供应链采购',
					modelCode: SceneConstType.MODEL_BUBUY,
					processList: ['发起采购决策'],
					iconImg: 'administrative-property.png',
					businessLineList: [
						{
							modelName: 'CSIG-服务器与供应链采购',
							modelCode: '',
							iconImg: 'content-design-drocurement.png'
						}
					]
				}
			]
		};
	}

	/**
	 * 依据场景代码获取中文描述
	 *
	 * @param {string} scene
	 * @returns {string}
	 *
	 * @memberOf SceneConstType
	 */
	public getSceneDescription(scene: string): string {
		// console.log('getSceneDescription..');
		const sceneOpton: ISceneOption = this.getSceneDataByCode(scene);
		let descName: string = sceneOpton.sceneName || '-';

		if (sceneOpton && sceneOpton.modeList && sceneOpton.modeList.length) {
			for (let index = 0; index < sceneOpton.modeList.length; index++) {
				const element: IModelOption = sceneOpton.modeList[index];

				if (element.modelCode === scene) {
					descName = element.modelName || '';
					break;
				}
			}
		}

		return descName;
	}

	/**
	 * 依据场景代码获取中文副标题描述
	 *
	 * @param {string} scene
	 * @returns {string}
	 *
	 * @memberOf SceneConstType
	 */
	public getSceneDescriptionBySub(scene: string): string {
		const sceneOpton: ISceneOption = this.getSceneDataByCode(scene);
		let descName: string = sceneOpton.sceneName || '-';

		if (sceneOpton && sceneOpton.modeList && sceneOpton.modeList.length) {
			for (let index = 0; index < sceneOpton.modeList.length; index++) {
				const element: IModelOption = sceneOpton.modeList[index];

				if (element.modelCode === scene) {
					descName = element.modelDesc || '';
					break;
				}
			}
		}

		return descName;
	}

	/**
	 * 依据场景模式编码和内容位置获取场景数据
	 *
	 * @param {string} sceneCode 场景代码
	 * @param {string} number	模式数组位置
	 * @returns {IModelOption}
	 *
	 * @memberOf SceneConstType
	 */
	public getModelSceneByIndex(sceneCode: string, index: number): IModelOption {
		const sceneData: ISceneOption = this.getSceneDataByCode(sceneCode);
		let modelData: IModelOption = {};

		if (sceneData) {
			const modelDataList: Array<IModelOption> = sceneData.modeList || [];

			if (modelDataList[index]) {
				modelData = modelDataList[index];
			}
		}

		return modelData;
	}

	/**
	 * 依据场景模式编码和内容编码获取场景数据
	 *
	 * @param {string} sceneCode 场景代码
	 * @param {string} modelCode 模式代码
	 * @returns {IModelOption}
	 *
	 * @memberOf SceneConstType
	 */
	public getModelSceneByCode(sceneCode: string, modelCode: string): IModelOption {
		const sceneData: ISceneOption = this.getSceneDataByCode(sceneCode);
		let modelData: IModelOption = {};

		if (sceneData) {
			const modelDataList: Array<IModelOption> = sceneData.modeList || [];

			for (let index = 0; index < modelDataList.length; index++) {
				const element: IModelOption = modelDataList[index];

				if (element.modelCode === modelCode) {
					modelData = element;
					break;
				}
			}
		}

		return modelData;
	}

	/**
	 * 依据场景KEY获取场景数据
	 *
	 * @private
	 * @param {string} sceneCode
	 * @returns {ISceneOption}
	 *
	 * @memberOf SceneConstType
	 */
	public getSceneDataByCode(sceneCode: string): ISceneOption {
		const sceneKey: string = `SCENE_${sceneCode.toUpperCase()}`;
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const thisVal: any = this;
		const sceneOptionData: any = thisVal[sceneKey];
		const sceneOption: ISceneOption = sceneOptionData;

		return sceneOption;
	}
}

/**
 * 场景数据结构
 *
 * @export
 * @interface ISceneOption
 */
export interface ISceneOption {
	sceneName?: string;
	sceneCode?: string;
	modeList?: Array<IModelOption> | undefined;
	iconImg?: string;
}

/**
 * 模式数据结构
 *
 * @export
 * @interface IModelOption
 */
export interface IModelOption {
	modelName?: string;
	modelDesc?: string;
	modelCode?: string;
	processList?: Array<string>;
	iconImg?: string;
	businessLineList?: Array<IModelOption>;
}

/** 选中场景数据 */
export interface ISelectSceneAndMode {
	selectSceneData?: ISceneOption;
	selectSceneModel?: IModelOption;
	businesslineData?: IModelOption;
}

/** 设备信息 */
// eslint-disable-next-line no-shadow
export enum DeviceType {
	Mobile,
	Desktop
}

/**
 * 上传列表更新
 */
export const ATTACHMENT_LIST_UPDATE: string = 'attachement-list-update';

/** app信息 */
export interface IAppState {
	device: DeviceType;
	sidebar: {
		opened: boolean;
		withoutAnimation: boolean;
	};
	language: string;
	size: string;
	sceneMode: ISceneMode;
}

/**
 * 场景模式数据结构
 *
 * @export
 * @interface ISceneMode
 */
export interface ISceneMode {
	/**
	 * 模式名
	 *
	 * @type {string}
	 * @memberOf ISceneMode
	 */
	model: string;

	/**
	 * 当景名
	 *
	 * @type {string}
	 * @memberOf ISceneMode
	 */
	scene: string;

	/** 业务线 */
	businessLine?: string;
}

/** app store */
@Module({
	dynamic: true,
	store,
	name: 'app'
})
class AppModule extends VuexModule implements IAppState {
	/**
	 * 子首页路径
	 */
	public INDEX_PAGE_PATH: string = '/home';

	/**
	 * 首页路径
	 */
	public ENTRANCE_PAGE_PATH: string = '/home/index';

	/**
	 * 首页路径内页
	 */
	public SECOND_PAGE_PATH: string = '/home/secondHomePage';

	// ===================== scnenMode =========================
	/**
	 * 场景数据
	 *
	 * @type {ISceneOption}
	 * @memberOf AppModule
	 */
	public sceneModelData: SceneConstType = new SceneConstType();

	/**
	 * 场景信息
	 *
	 * @type {ISceneMode}
	 * @memberOf App
	 */
	public bgSourceData: any = {};

	/** 设置面包屑显示与否 */
	@Mutation
	private SET_BG_SELECTED(bgSourceData: any): any {
		this.bgSourceData = bgSourceData;
	}

	/** action 设置BG代码 */
	@Action
	public setBgSelected(bgSourceData: any): any {
		this.SET_BG_SELECTED(bgSourceData);
	}

	/**
	 * 场景信息
	 *
	 * @type {ISceneMode}
	 * @memberOf App
	 */
	public sceneMode: ISceneMode = {
		model: SceneConstType.MODEL_CDP,
		scene: SceneConstType.SCENE_QCP,
		businessLine: ''
	};

	/** 设置面包屑显示与否 */
	@Mutation
	private SET_SCENE_MODE(sceneMode: ISceneMode): any {
		this.sceneMode = sceneMode;
	}

	/** action 设置面包屑显示与否 */
	@Action
	public setSceneMode(sceneMode: ISceneMode): any {
		this.SET_SCENE_MODE(sceneMode);
	}

	/**
	 * 是否已向后端设置模式场景
	 *
	 * @type {ISceneMode}
	 * @memberOf App
	 */
	public isSceneModeSetted: boolean = false;

	/** 设置是否已向后端设置模式场景 */
	@Mutation
	private SET_IS_SCENE_MODE_SETTED(isSceneModeSetted: boolean): any {
		this.isSceneModeSetted = isSceneModeSetted;
	}

	/** action 设置是否已向后端设置模式场景 */
	@Action
	public setIsSceneModeSetted(isSceneModeSetted: boolean): any {
		this.SET_IS_SCENE_MODE_SETTED(isSceneModeSetted);
	}

	/**
	 * 场景信息
	 *
	 * @type {ISceneMode}
	 * @memberOf App
	 */
	public selectSceneAndMode: ISelectSceneAndMode = {};

	/** 设置面包屑显示与否 */
	@Mutation
	private SET_SELECT_SCENE_AND_MODE(payload: ISelectSceneAndMode): any {
		this.selectSceneAndMode = payload;
	}

	/** action 设置面包屑显示与否 */
	@Action
	public saveSelectSceneAndMode(payload: ISelectSceneAndMode): any {
		this.SET_SELECT_SCENE_AND_MODE(payload);
	}

	/**
	 * 场景选择状态
	 * 0 初始状态
	 * 1 选择了BG
	 * 2 选择了模式
	 * 3 选择了场景
	 * 4 选择了业务线
	 *
	 * @type {ISceneMode}
	 * @memberOf App
	 */
	public scneneSelectStatus: number = 0;

	/** 场景选择状态 mutation */
	@Mutation
	private SET_SCNENE_SELECT_STATUS(payload: number): any {
		this.scneneSelectStatus = payload;
	}

	/** action 场景选择状态 */
	@Action
	public saveScneneSelectStatus(payload: number): any {
		this.SET_SCNENE_SELECT_STATUS(payload);
	}
	// ===================== scnenMode =========================

	// ===================== showBreads ========================
	/**
	 * 显示面包屑
	 *
	 * @type {boolean}
	 * @memberOf App
	 */
	public showBreads: boolean = true;

	/** 设置面包屑显示与否 */
	@Mutation
	private SET_SHOW_BREADS(showBreads: boolean): any {
		this.showBreads = showBreads;
	}

	/** action 设置面包屑显示与否 */
	@Action
	public setShowBreads(showBreads: boolean): any {
		this.SET_SHOW_BREADS(showBreads);
	}

	/**
	 * 是否在入口页标记
	 *
	 * @type {boolean}
	 * @memberOf App
	 */
	public entranceFlag: boolean = false;

	/** 设置是否在入口页标记 */
	@Mutation
	private SET_ENTRANCE_FLAG(entranceFlag: boolean): any {
		this.entranceFlag = entranceFlag;
	}

	/** action 是否在入口页标记 */
	@Action
	public setEntranceFlag(entranceFlag: boolean): any {
		this.SET_ENTRANCE_FLAG(entranceFlag);
	}
	// ===================== showBreads ========================

	// ===================== sidebar ===========================
	/** 侧边栏 */
	public sidebar: any = {
		opened: getSidebarStatus() !== 'closed',
		withoutAnimation: false
	};

	/** toogle sidebar */
	@Mutation
	private TOGGLE_SIDEBAR(withoutAnimation: boolean): any {
		this.sidebar.opened = !this.sidebar.opened;
		this.sidebar.withoutAnimation = withoutAnimation;
		if (this.sidebar.opened) {
			setSidebarStatus('opened');
		} else {
			// setSidebarStatus('closed');
		}
	}

	/** close sidebar */
	@Mutation
	private CLOSE_SIDEBAR(withoutAnimation: boolean): any {
		this.sidebar.opened = false;
		this.sidebar.withoutAnimation = withoutAnimation;
		// setSidebarStatus('closed');
	}

	/** action toogle sidebar */
	@Action
	public toggleSideBar(withoutAnimation: boolean): any {
		this.TOGGLE_SIDEBAR(withoutAnimation);
	}

	/** action close sidebar */
	@Action
	public closeSideBar(withoutAnimation: boolean): any {
		this.CLOSE_SIDEBAR(withoutAnimation);
	}
	// ===================== sidebar ===========================

	// ===================== device ============================
	/** 设备 */
	public device: any = DeviceType.Desktop;

	/** toogle device */
	@Mutation
	private TOGGLE_DEVICE(device: DeviceType): any {
		this.device = device;
	}

	/** toogle device */
	@Action
	public toggleDevice(device: DeviceType): any {
		this.TOGGLE_DEVICE(device);
	}
	// ===================== device ============================

	// ===================== language ==========================
	/** 语言 */
	public language: any = getLocale();

	/** 设置语言 */
	@Mutation
	private SET_LANGUAGE(language: string): any {
		this.language = language;
		setLanguage(this.language);
	}

	/** action 设置语言 */
	@Action
	public setLanguage(language: string): any {
		this.SET_LANGUAGE(language);
	}
	// ===================== language ==========================

	// ===================== size ==============================
	/** size */
	public size: any = getSize() || 'medium';

	/** set size */
	@Mutation
	private SET_SIZE(size: string): any {
		this.size = size;
		setSize(this.size);
	}

	/** action set size */
	@Action
	public setSize(size: string): any {
		this.SET_SIZE(size);
	}
	// ===================== size ==============================

	// ===================== 403 change ==============================
	/** forbiddenChange */
	public forbiddenChange: IForbiddenData = { isForbidden: false, error: null };

	/** set forbiddenChange */
	@Mutation
	private FORBIDDEN_CHANGE_UPDATE(val: IForbiddenData): any {
		this.forbiddenChange = val;
	}

	/** action set forbiddenChange */
	@Action
	public forbiddenChangeUpdate(val: IForbiddenData): any {
		this.FORBIDDEN_CHANGE_UPDATE(val);
	}
	// ===================== 403 change ==============================

	// ===================== 上传组件公共数据 =====================
	/** 上传组件公共数据 */
	public uploadAttachmentList: Array<IAttachmentList> = [];

	/** IDC上传公共数据 */
	public uploadAttachmentGroupLists: Array<IAttachmentTableData> = [];

	/** 保存上传组件公共数据 Mutation */
	@Mutation
	private SET_UPLOAD_ATTACHMENT_LIST(list: Array<IAttachmentList>): any {
		this.uploadAttachmentList = list;
	}

	/** IDC保存上传组件公共数据 Mutation */
	@Mutation
	private SET_IDC_UPLOAD_ATTACHMENT_LIST(list: Array<IAttachmentTableData>): any {
		this.uploadAttachmentGroupLists = list;
	}

	/** 保存上传组件公共数据 Action */
	@Action
	public saveUploadAttachmentList(list: Array<IAttachmentList>): any {
		this.SET_UPLOAD_ATTACHMENT_LIST(list);
	}

	/** IDC 上传组件公共 Action */
	@Action
	public saveUploadAttachmentGroupLists(list: Array<IAttachmentTableData>): any {
		this.SET_IDC_UPLOAD_ATTACHMENT_LIST(list);
	}

	/** 更新上传组件公共数据 */
	public updateUploadAttachmentData: IAttachmentEvent = {
		type: '',
		list: []
	};

	/** 更新上传组件公共数据 Mutation */
	@Mutation
	public UPDATE_UPLOAD_ATTACHMENT_LIST(payload: IAttachmentEvent): void {
		this.updateUploadAttachmentData = payload;
	}

	/** 更新上传组件公共数据 Action */
	@Action
	public updateUploadAttachmentList(payload: IAttachmentEvent): void {
		this.UPDATE_UPLOAD_ATTACHMENT_LIST(payload);
	}

	/** ==================== 供应商短名单 上传组件公共数据=========================== */
	/** 供应商短名单上传公共数据 */
	public supplierUploadAttachmentList: Array<IAttachmentTableData> = [];

	/** 供应商短名 单保存上传组件公共数据 Mutation */
	@Mutation
	private SET_SUPPLIER_UPLOAD_ATTACHMENT_LIST(list: Array<IAttachmentTableData>): any {
		this.supplierUploadAttachmentList = list;
	}

	/** 供应商短名单 上传组件公共 Action */
	@Action
	public saveSupplierUploadAttachmentList(list: Array<IAttachmentTableData>): any {
		this.SET_SUPPLIER_UPLOAD_ATTACHMENT_LIST(list);
	}

	/** ====================== 业务需求或决策单的补充附件 ======================== */

	/** 是否是补充附件 */
	public isSupplementaryAnnex: boolean | undefined = false;
	/** 补充附件数据 */
	public supplementaryAnnex: Array<IAttachmentTableData> = [];
	/** 补充附件数据更新 */
	public updateSupplementaryAnnexLists: IAttachmentEvent = {
		type: '',
		list: []
	};

	/** 附件模式(true：补充附件模式，false：普通模式) */
	@Mutation
	private SET_SUPPLEMENTARY_ANNEX(data: boolean | undefined): any {
		this.isSupplementaryAnnex = data;
	}

	/** 触发改变附件模式 */
	@Action
	public saveSupplementaryAnnex(data: boolean | undefined): any {
		this.SET_SUPPLEMENTARY_ANNEX(data);
	}

	/** 保存补充附件公共数据 Mutation */
	@Mutation
	private SET_SUPPLEMENTARY_ANNEX_LIST(list: Array<IAttachmentTableData>): any {
		this.supplementaryAnnex = list;
	}

	/** 保存补充附件公共数据 Action */
	@Action
	public saveSupplementaryAnnexList(list: Array<IAttachmentTableData>): any {
		this.SET_SUPPLEMENTARY_ANNEX_LIST(list);
	}

	/** 更新保存补充附件 Mutation */
	@Mutation
	private UPDATE_SUPPLEMENTARY_ANNEX_LIST(payload: IAttachmentEvent): void {
		this.updateSupplementaryAnnexLists = payload;
	}

	/** 更新保存补充附件 Action */
	@Action
	public updateSupplementaryAnnexList(payload: IAttachmentEvent): void {
		this.UPDATE_SUPPLEMENTARY_ANNEX_LIST(payload);
	}

	/** ===========================IDC 更新附件 =============================== */
	/** IDC更新上传公共数据 */
	public updateAttachmentGroupLists: IAttachmentEvent = {
		type: '',
		list: []
	};

	/** IDC 更新上传组件公共数据 Mutation */
	@Mutation
	private UPDATE_IDC_UPLOAD_ATTACHMENT_LIST(payload: IAttachmentEvent): void {
		this.updateAttachmentGroupLists = payload;
	}

	/** IDC 更新上传组件公共数据 Action */
	@Action
	public updateUploadIAttachmentEventIDC(payload: IAttachmentEvent): void {
		this.UPDATE_IDC_UPLOAD_ATTACHMENT_LIST(payload);
	}

	/** ====================== 供应商短名单 更新============================== */
	/** 供应商短名单 更新上传公共数据 */
	public updateAttachmentSupplierLists: IAttachmentEvent = {
		type: '',
		list: []
	};

	/** 供应商短名单 更新上传组件公共数据 Mutation */
	@Mutation
	private UPDATE_SUPPLIER_UPLOAD_ATTACHMENT_LIST(payload: IAttachmentEvent): void {
		this.updateAttachmentSupplierLists = payload;
	}

	/** 供应商短名单 更新上传组件公共数据 Action */
	@Action
	public updateUploadIAttachmentEventSupplier(payload: IAttachmentEvent): void {
		this.UPDATE_SUPPLIER_UPLOAD_ATTACHMENT_LIST(payload);
	}

	/** 上传内容标识ID */
	public uplpadFlagID: any = Date.now();

	/** 上传内容标识ID Mutation */
	@Mutation
	public SAVE_UPLOAD_FLAG_ID(payload: any): void {
		this.uplpadFlagID = payload;
	}

	/** 上传内容标识ID Action */
	@Action
	public saveUploadFlagID(payload: any): void {
		this.SAVE_UPLOAD_FLAG_ID(payload);
	}
	// ===================== 上传组件公共数据 ======================

	/**
	 * 是否显示遮罩
	 */
	public showModal: boolean = true;

	/** 改变显示遮罩状态 Mutation */
	@Mutation
	private SAVE_SHOW_MODAL_STATUS(payload: boolean): void {
		this.showModal = payload;
	}

	/** 改变显示遮罩状态 Action */
	@Action
	public saveShowModalStatus(payload: boolean): void {
		this.SAVE_SHOW_MODAL_STATUS(payload);
	}
}

export const appModule: AppModule = getModule(AppModule);
